import { useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import {
  FiX,
  FiUser,
  FiPhone,
  FiCalendar,
  FiCamera,
  FiCheckCircle,
  FiCircle,
} from "react-icons/fi"
import { BsGenderAmbiguous } from "react-icons/bs"

/* ── Fields to check ─────────────────────────────────────────── */
const FIELDS = [
  {
    key: "firstName",
    label: "First Name",
    icon: FiUser,
    check: (u) => !!u?.firstName?.trim(),
  },
  {
    key: "lastName",
    label: "Last Name",
    icon: FiUser,
    check: (u) => !!u?.lastName?.trim(),
  },
  {
    key: "phone",
    label: "Phone Number",
    icon: FiPhone,
    check: (u) => !!u?.additionalDetails?.contactNumber,
  },
  {
    key: "dob",
    label: "Date of Birth",
    icon: FiCalendar,
    check: (u) => !!u?.additionalDetails?.dateOfBirth,
  },
  {
    key: "gender",
    label: "Gender",
    icon: BsGenderAmbiguous,
    check: (u) => !!u?.additionalDetails?.gender,
  },
  {
    key: "image",
    label: "Profile Photo",
    icon: FiCamera,
    check: (u) => !!u?.image,
  },
]

const SESSION_KEY = "profile_prompt_dismissed"

export default function ProfileCompleteModal() {
  const { user } = useSelector((state) => state.profile)
  const navigate = useNavigate()
  const location = useLocation()

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "1"
  )

  /* Don't show on settings page — user is already fixing it */
  const onSettings = location.pathname.includes("/dashboard/settings")

  const missing = FIELDS.filter((f) => !f.check(user))
  const filled = FIELDS.length - missing.length
  const pct = Math.round((filled / FIELDS.length) * 100)

  /* Nothing to show */
  if (!user || missing.length === 0 || dismissed || onSettings) return null

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1")
    setDismissed(true)
  }

  const handleComplete = () => {
    handleDismiss()
    navigate("/dashboard/settings")
  }

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-richblack-900/70 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-richblack-600 bg-richblack-800 shadow-2xl">

        {/* Close */}
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-richblack-5"
          aria-label="Dismiss"
        >
          <FiX size={18} />
        </button>

        {/* Top accent bar */}
        <div className="h-1 w-full bg-richblack-700">
          <div
            className="h-full bg-yellow-50 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="p-6">
          {/* Avatar + heading */}
          <div className="mb-5 flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <img
                src={
                  user?.image ||
                  `https://api.dicebear.com/5.x/initials/svg?seed=${user?.firstName}`
                }
                alt={user?.firstName}
                className="h-14 w-14 rounded-full object-cover ring-2 ring-richblack-600"
              />
              {/* incomplete indicator */}
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-50 text-richblack-900 text-[10px] font-bold shadow">
                !
              </span>
            </div>
            <div>
              <h2 className="text-base font-bold text-richblack-5">
                Complete your profile
              </h2>
              <p className="text-xs text-richblack-400">
                {filled} of {FIELDS.length} fields filled &mdash;{" "}
                <span className="font-semibold text-yellow-50">{pct}% done</span>
              </p>
            </div>
          </div>

          {/* Field checklist */}
          <div className="mb-5 grid grid-cols-2 gap-2">
            {FIELDS.map((field) => {
              const done = field.check(user)
              const Icon = field.icon
              return (
                <div
                  key={field.key}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                    done
                      ? "bg-caribbeangreen-300/10 text-caribbeangreen-300"
                      : "bg-richblack-700 text-richblack-300"
                  }`}
                >
                  {done ? (
                    <FiCheckCircle size={13} className="flex-shrink-0" />
                  ) : (
                    <FiCircle size={13} className="flex-shrink-0 text-richblack-500" />
                  )}
                  <Icon size={12} className="flex-shrink-0" />
                  <span className={done ? "line-through opacity-60" : ""}>
                    {field.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Missing fields hint */}
          {missing.length > 0 && (
            <p className="mb-5 rounded-lg bg-yellow-50/5 border border-yellow-50/10 px-3 py-2 text-xs text-richblack-300">
              Missing:{" "}
              <span className="font-medium text-yellow-50">
                {missing.map((f) => f.label).join(", ")}
              </span>
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleComplete}
              className="flex-1 rounded-xl bg-yellow-50 py-2.5 text-sm font-bold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95"
            >
              Complete Profile
            </button>
            <button
              onClick={handleDismiss}
              className="rounded-xl border border-richblack-600 px-4 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
